import * as React from 'react';
import { assertUnreachable } from '../utilities/TypeScriptHelpers';

export type AsyncFetchState<T> = {
  data: T | null;
  loading: boolean;
  error: unknown | undefined;
};

export type AsyncFetchResult<T> = {
  data: T | null;
  loading: boolean;
  error: unknown | undefined;
  refetch: () => void;
}

/**
 * This hook performs an async operation and stores its result in component state. It protects
 * against the common pitfalls of trying to set state on an unmounted or stale component.
 *
 * This hook is okay for simple cases, but for more complex use cases, consider using a more robust
 * library such as react-query or apollo.
 *
 * The hook returns the following information:
 * - `data`: the most recent valid (successfully-fetched) data. Will be null before first fetch.
 *   _This will still contain the last valid data_ during subsequent loading states or when the most
 *   recent fetch returned an error, even though that data no longer corresponds to the current
 *   params.
 * - `loading`: a boolean that will be true while a fetch is in progress (either on initial load
 *   or during a refetch after the params change)
 * - `error`: if the most recent invocation of the fetch returned an error, it will be populated
 *   here.
 * - `refetch`: a function that can be used to trigger a refetch (for example, if the client
 *   made an update via other means). During the refetch, the old data/error will still be
 *   available.
 *
 * @param fetcher
 * @param params
 */
export function useAsyncFetch<P extends unknown[], T>(
  fetcher: (...params: P) => PromiseLike<T>,
  ...params: P
): AsyncFetchResult<T> {

  const [refetchKey, setRefetchKey] = React.useState(0);

  const refetch = React.useCallback(
    () => setRefetchKey(old => old + 1),
    [setRefetchKey]
  );

  const [state, dispatch] = React.useReducer<React.Reducer<AsyncFetchState<T> , AsyncFetchAction<T>>>(
    asyncFetchReducer,
    { loading: true, data: null, error: undefined }
  );

  React.useEffect(
    () => {

      // The effect will be initiated only when the component is under valid conditions (e.g. rendered).
      // Once the effect becomes stale (either via unmount or params change), `queryStatus` gets set
      // to `stale` in the cleanup. A stale query status signifies that the fetch result should be
      // discarded.
      let queryStatus: 'valid' | 'stale' = 'valid';

      async function fetchTheData() {
        dispatch({ type: 'START_FETCH' });
        try {
          const data = await fetcher(...params);
          if (queryStatus === 'valid') {
            dispatch({ type: 'FETCH_SUCCESSFUL', data });
          }
        } catch (e) {
          if (queryStatus === 'valid') {
            dispatch({ type: 'FETCH_FAILED', error: e });
          }
        }
      }

      void fetchTheData();
      return () => {
        queryStatus = 'stale'
      };
    },
    // We're using the spread of the params inside the fetcher,
    // so that's what we pass to the deps array too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fetcher, refetchKey, ...params]
  );

  return {
    loading: state.loading,
    data: state.data,
    error: state.error,
    refetch
  };
}

type AsyncFetchAction<T, E = unknown> =
  | { type: 'START_FETCH' }
  | { type: 'FETCH_SUCCESSFUL', data: T }
  | { type: 'FETCH_FAILED', error: E | undefined }

function asyncFetchReducer<T>(
  prevState: AsyncFetchState<T>,
  action: AsyncFetchAction<T>
): AsyncFetchState<T> {
  switch (action.type) {
    case 'START_FETCH': return {
      loading: true,
      data: prevState.data,
      error: prevState.error,
    };
    case 'FETCH_SUCCESSFUL': return {
      loading: false,
      data: action.data,
      error: undefined,
    };
    case 'FETCH_FAILED': return {
      loading: false,
      data: prevState.data,
      error: action.error,
    };
    default:
      assertUnreachable(action)
      return prevState;
  }
}
